import { CONTAINER_INVALIDATED_EVENT, type ContainerLifecycleDetail } from './types';

/**
 * Invalidate a federation container — remove from window, clean up script tags,
 * and dispatch a lifecycle event so listeners (e.g. useParcelRegistry) re-scan.
 *
 * Classic path only touches state that exists on the classic path: on the MF2
 * runtime path there is no `window[scope]` container and no injected `<script>`,
 * so those two steps are harmless no-ops. Runtime re-fetch ("eviction") is not
 * done here — the runtime instance is loader-scoped, not reachable from this pure
 * helper — but at `ParcelLoader.doReload` via `registerRemotes(..., {force:true})`
 * with a cache-busted entry. The event dispatch below drives the reload on both.
 *
 * @param scope - The federation scope key (e.g. '@abax/central/installer-ui')
 */
export function invalidateContainer(scope: string): void {
  // 1. Delete the container from window
  const win = window as unknown as Record<string, unknown>;
  if (scope in win) {
    delete win[scope];
  }

  // 2. Remove associated <script> tags (tagged by loadRemoteEntry with data-parcel-scope)
  const taggedScripts = document.querySelectorAll<HTMLScriptElement>(
    `script[data-parcel-scope="${CSS.escape(scope)}"]`,
  );
  taggedScripts.forEach((el) => el.remove());

  // 3. Dispatch invalidation event
  window.dispatchEvent(
    new CustomEvent<ContainerLifecycleDetail>(CONTAINER_INVALIDATED_EVENT, {
      detail: { scope, timestamp: Date.now() },
    }),
  );
}
